<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCartStore } from '../stores/cart';
import CartErrors from './CartErrors.vue';
import CartLineItems from './CartLineItems.vue';
import CartCoupon from './CartCoupon.vue';
import CartTotals from './CartTotals.vue';
import xmarkIcon from '@fortawesome/fontawesome-pro/svgs/solid/xmark.svg?raw';
import cartIllustration from '../../../images/cart-illustration.svg?raw';
import { CART_POPOVER_ID } from '../constants';

const { t } = useI18n({ useScope: 'global' });
const cart = useCartStore();
const popoverEl = ref<HTMLElement | null>(null);

const defaultLanguage = 'de';
const currentLanguage = document.documentElement.lang || defaultLanguage;
const urlPrefix = currentLanguage === defaultLanguage ? '' : `/${currentLanguage}`;
</script>

<template>
    <div
        :id="CART_POPOVER_ID"
        ref="popoverEl"
        popover="auto"
        class="cart-slideout"
        role="dialog"
        aria-labelledby="cart-slideout-title"
    >
        <div class="cart-slideout__header">
            <h2 id="cart-slideout-title" class="cart-slideout__title">
                {{ (cart.cart?.totalQty ?? 0) > 0
                    ? t('cart.title', { count: cart.cart?.totalQty })
                    : t('cart.titleEmpty')
                }}
            </h2>
            <button type="button" class="cart-slideout__close" @click="popoverEl?.hidePopover()">
                <span class="visually-hidden">{{ t('cart.close') }}</span>
                <span aria-hidden="true" v-html="xmarkIcon" />
            </button>
        </div>

        <div class="cart-slideout__content">
            <div class="cart-slideout__body">
                <div v-if="cart.cart === null && cart.errors === null" class="cart-slideout__loading">
                    {{ t('cart.loading') }}
                </div>

                <div v-else-if="cart.cart?.lineItems.length === 0" class="cart-slideout__empty">
                    <span class="cart-slideout__empty-illustration" aria-hidden="true" v-html="cartIllustration" />
                    <p class="cart-slideout__empty-text">{{ t('cart.empty') }}</p>
                    <a :href="`${urlPrefix}/shop`" class="btn-cta cart-slideout__empty-cta">{{ t('cart.emptyShopCta') }}</a>
                </div>

                <div v-if="cart.errors" class="mt-3 mx-4">
                    <CartErrors v-if="cart.errors" :errors="cart.errors" />
                </div>

                <ul
                    v-if="cart.cart?.notices.length"
                    class="cart-slideout__notices"
                    aria-live="polite"
                    aria-atomic="false"
                >
                    <li v-for="notice in cart.cart.notices" :key="notice.id" class="alert alert-warning cart-slideout__notice">
                        {{ notice.message }}
                    </li>
                </ul>

                <CartLineItems />
            </div>

            <div v-if="(cart.cart?.lineItems ?? []).length > 0" class="cart-slideout__summary">
                <CartCoupon />

                <div class="cart-slideout__totals">
                    <CartTotals />
                </div>

                <div class="cart-slideout__footer">
                    <a :href="`${urlPrefix}/shop/checkout`" class="btn-cta cart-slideout__checkout">
                        {{ t('cart.checkout') }}
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>
